/**
 * Models API Service
 * Frontend API client for managing Azure OpenAI model configurations
 * NOTE: All DB-managed models are chat models
 */
import { fetcher } from '@/config/api';

export interface Model {
  id: string;
  name: string;
  endpoint: string;
  deployment: string;
  api_version: string;
  api_path: string;
  is_implicit: boolean;
  is_active: boolean;
  supports_images: boolean;
  description?: string;
  max_tokens?: number;
  pricing_input_1m_tokens: number;
  pricing_output_1m_tokens: number;
  created_by: string;
  created_date: string;
  updated_date: string;
}

export interface ModelCreate {
  name: string;
  endpoint: string;
  deployment: string;
  api_version: string;
  api_path: string;
  is_implicit?: boolean;
  is_active?: boolean;
  supports_images?: boolean;
  description?: string;
  max_tokens?: number;
  pricing_input_1m_tokens: number;
  pricing_output_1m_tokens: number;
}

export interface ModelUpdate {
  name?: string;
  endpoint?: string;
  deployment?: string;
  api_version?: string;
  api_path?: string;
  is_implicit?: boolean;
  is_active?: boolean;
  supports_images?: boolean;
  description?: string;
  max_tokens?: number;
  pricing_input_1m_tokens?: number;
  pricing_output_1m_tokens?: number;
}

export interface ModelsListResponse {
  models: Model[];
  total: number;
  page: number;
  per_page: number;
}

export interface ModelStats {
  total_models: number;
  active_models: number;
  implicit_models: number;
}

export interface ModelsFilters {
  is_active?: boolean;
  is_implicit?: boolean;
  page?: number;
  per_page?: number;
}

// Legacy model interface for backward compatibility
export interface LegacyModel {
  id: string;
  name: string;
  description: string;
}

export class ModelsAPI {
  /**
   * Get models list with optional filtering
   */
  async getModels(filters: ModelsFilters = {}): Promise<ModelsListResponse> {
    const params = new URLSearchParams();
    
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters.is_implicit !== undefined) params.append('is_implicit', filters.is_implicit.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    
    const queryString = params.toString();
    const url = `/api/models${queryString ? `?${queryString}` : ''}`;
    
    return fetcher<ModelsListResponse>(url);
  }

  /**
   * Get models statistics
   */
  async getModelsStats(): Promise<ModelStats> {
    return fetcher<ModelStats>(`/api/models/stats`);
  }

  /**
   * Get all implicit models (accessible to all users)
   */
  async getImplicitModels(): Promise<Model[]> {
    return fetcher<Model[]>(`/api/models/implicit`);
  }

  /**
   * Get all models accessible to the current user
   * Returns models that are either implicit or explicitly granted via permissions
   */
  async getAccessibleModels(): Promise<Model[]> {
    return fetcher<Model[]>(`/api/models/accessible`);
  }

  /**
   * Get a specific model by ID
   */
  async getModel(modelId: string): Promise<Model> {
    return fetcher<Model>(`/api/models/${modelId}`);
  }

  /**
   * Create a new model
   */
  async createModel(modelData: ModelCreate): Promise<Model> {
    return fetcher<Model>(`/api/models`, {
      method: 'POST',
      body: JSON.stringify(modelData),
    });
  }

  /**
   * Update a model
   */
  async updateModel(modelId: string, updates: ModelUpdate): Promise<Model> {
    return fetcher<Model>(`/api/models/${modelId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Delete a model
   */
  async deleteModel(modelId: string): Promise<{ message: string }> {
    return fetcher<{ message: string }>(`/api/models/${modelId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Toggle model active status
   */
  async toggleModelActive(modelId: string): Promise<{ message: string; model: Model }> {
    return fetcher<{ message: string; model: Model }>(`/api/models/${modelId}/toggle-active`, {
      method: 'POST',
    });
  }

  /**
   * Toggle model implicit status
   */
  async toggleModelImplicit(modelId: string): Promise<{ message: string; model: Model }> {
    return fetcher<{ message: string; model: Model }>(`/api/models/${modelId}/toggle-implicit`, {
      method: 'POST',
    });
  }

  /**
   * Get available model types
   */
  async getModelTypes(): Promise<string[]> {
    return fetcher<string[]>(`/api/models/types`);
  }
}

// Export singleton instance
export const modelsAPI = new ModelsAPI();

// Legacy functions for backward compatibility
// Legacy helpers removed. Use ModelsAPI.getModels() for DB-backed models.

// Simple helpers for agents/features endpoints
export async function fetchAgents(): Promise<any[]> {
  return fetcher<any[]>('/api/agents');
}

export async function fetchAccessibleAgents(): Promise<any[]> {
  return fetcher<any[]>('/api/agents/accessible');
}

export async function fetchFeatures(): Promise<any[]> {
  return fetcher<any[]>('/api/features');
}

export async function fetchAccessibleFeatures(): Promise<any[]> {
  return fetcher<any[]>('/api/features/accessible');
}

// Requestable endpoints for permission requests (available to all users)
export async function fetchRequestableModels(): Promise<Model[]> {
  return fetcher<Model[]>('/api/models/requestable');
}

export async function fetchRequestableAgents(): Promise<any[]> {
  return fetcher<any[]>('/api/agents/requestable');
}

export async function fetchRequestableFeatures(): Promise<any[]> {
  return fetcher<any[]>('/api/features/requestable');
}

export async function createAgent(agent: any): Promise<any> {
  return fetcher<any>('/api/agents', { method: 'POST', body: JSON.stringify(agent) });
}

export async function createFeature(feature: any): Promise<any> {
  return fetcher<any>('/api/features', { method: 'POST', body: JSON.stringify(feature) });
}

export async function updateAgent(agentId: string, updates: any): Promise<any> {
  return fetcher<any>(`/api/agents/${agentId}`, { method: 'PATCH', body: JSON.stringify(updates) });
}

export async function updateFeature(featureId: string, updates: any): Promise<any> {
  return fetcher<any>(`/api/features/${featureId}`, { method: 'PATCH', body: JSON.stringify(updates) });
}

export async function deleteAgent(agentId: string): Promise<void> {
  await fetcher<void>(`/api/agents/${agentId}`, { method: 'DELETE' });
}

export async function deleteFeature(featureId: string): Promise<void> {
  await fetcher<void>(`/api/features/${featureId}`, { method: 'DELETE' });
}

export async function toggleAgentActive(agentId: string): Promise<any> {
  return fetcher<any>(`/api/agents/${agentId}/toggle-active`, { method: 'POST' });
}

export async function toggleFeatureActive(featureId: string): Promise<any> {
  return fetcher<any>(`/api/features/${featureId}/toggle-active`, { method: 'POST' });
}

export async function toggleAgentImplicit(agentId: string): Promise<any> {
  return fetcher<any>(`/api/agents/${agentId}/toggle-implicit`, { method: 'POST' });
}

export async function toggleFeatureImplicit(featureId: string): Promise<any> {
  return fetcher<any>(`/api/features/${featureId}/toggle-implicit`, { method: 'POST' });
}